import { IconArrowLeft, IconDeviceFloppy } from "@tabler/icons-react"
import { type FC, useMemo, useState } from "react"
import { Link, Navigate, useParams } from "react-router"

import { type BokaderoVideo } from "@forento/core/models/bokadero"
import { parseNumber } from "@forento/core/utilities/number"
import LexicalViewer from "@forento/ui/components/LexicalViewer"

import Layout from "~/components/layout"
import Loader from "~/components/loader"
import RichContentEditor from "~/components/rich-content-editor"
import {
	exportEditedRichContent,
	loadRichContent,
	parseRichContent,
	type RichContentValue,
} from "~/utilities/rich-content"
import { query } from "~/utilities/trpc"

const VideoPage: FC = () => {
	const videoId = parseNumber(useParams().videoId)

	const { data: video, error } = query.getVideo.useQuery(videoId ?? -1)

	if (video === null) {
		return <Navigate to="/videos" />
	}

	return (
		<Layout>
			{error ? (
				<div className="mx-auto flex w-full max-w-4xl flex-col gap-8 py-10">
					<div className="flex flex-col items-center justify-center py-10">
						<span className="text-lg font-medium text-slate-600">Kunde inte ladda video</span>
						<Link to="/videos" className="text-primary mt-4 hover:underline">
							← Tillbaka till videor
						</Link>
					</div>
				</div>
			) : video === undefined ? (
				<div className="mx-auto flex w-full max-w-4xl flex-col gap-8 py-10">
					<Loader label="Laddar video..." />
				</div>
			) : (
				<Content video={video} />
			)}
		</Layout>
	)
}

const Content: FC<{ video: BokaderoVideo }> = ({ video }) => {
	const [presentation, setPresentation] = useState<RichContentValue | null>(loadRichContent(video.presentation))

	const updateVideoMutation = query.updateVideo.useMutation({
		onError() {
			alert("Ett fel uppstod. Försök igen.")
		},
	})

	function handleSave() {
		const exportedPresentation = exportEditedRichContent(presentation)
		if (exportedPresentation === undefined) return

		updateVideoMutation.mutate({ videoId: video.id, presentation: exportedPresentation })
	}

	const initialValue = useMemo(() => parseRichContent(video.presentation), [video.presentation])

	return (
		<div className="mx-auto flex w-full max-w-4xl flex-col gap-8 py-10">
			<div className="flex items-center gap-4">
				<Link to="/videos" className="text-primary hover:underline">
					<IconArrowLeft className="h-5 w-5" />
				</Link>
				<h1 className="text-3xl font-semibold text-slate-900">{video.title}</h1>
			</div>

			<div className="space-y-8">
				<div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
					<div className="mb-6">
						<Thumbnail url={video.thumbnailFilePath} title={video.title} />
					</div>

					<div className="space-y-4">
						<div>
							<h3 className="text-lg font-medium text-slate-900">Beskrivning</h3>
							<p className="mt-2 text-slate-600">
								{video.shortDescription || "Ingen beskrivning tillgänglig"}
							</p>
						</div>

						{video.longDescriptionHtml && (
							<div>
								<h3 className="text-lg font-medium text-slate-900">Detaljerad beskrivning</h3>
								<LexicalViewer html={video.longDescriptionHtml} className="mt-2 text-slate-600" />
							</div>
						)}
					</div>
				</div>

				<div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
					<h3 className="text-lg font-medium text-slate-900">Presentation</h3>

					<div className="mt-4 space-y-4">
						<RichContentEditor
							placeholder="Skriv din presentation här..."
							initialValue={initialValue}
							setValue={setPresentation}
						/>
						<button
							type="button"
							onClick={handleSave}
							disabled={updateVideoMutation.isPending}
							className="bg-primary hover:bg-primary/90 flex cursor-pointer items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
						>
							<IconDeviceFloppy className="size-4" />
							{updateVideoMutation.isPending ? "Sparar..." : "Spara"}
						</button>
					</div>
				</div>
			</div>
		</div>
	)
}

const Thumbnail: FC<{ url: string | null; title: string }> = ({ url, title }) => (
	<div className="flex justify-center">
		{url ? (
			<div className="flex aspect-video w-full max-w-full items-center justify-center overflow-hidden rounded border border-slate-200 bg-slate-100">
				<img src={url} alt={`Thumbnail för ${title}`} className="h-full w-full object-cover" />
			</div>
		) : (
			<div className="flex aspect-video w-full max-w-full items-center justify-center rounded border border-slate-200 bg-slate-100 text-slate-300">
				<span className="text-4xl">🎥</span>
			</div>
		)}
	</div>
)

export default VideoPage
